import { useEffect, useState } from "react";
import { Megaphone, PartyPopper, Power, Trash2, Trophy } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { BulkBar, bulkBtnCls } from "@/components/admin/BulkBar";

const inputCls =
  "h-11 w-full rounded-xl border border-slate-200 bg-paper px-4 text-sm font-medium text-ink outline-none transition-colors duration-200 focus:border-royal";

const empty = { title: "", body: "", cta_text: "", cta_url: "" };

export const PromosTab = () => {
  const [promos, setPromos] = useState([]);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [drawing, setDrawing] = useState({ pool: [], total_entries: 0, last_draw: null });
  const [winner, setWinner] = useState(null);
  const [drawBusy, setDrawBusy] = useState(false);
  const [promoFilter, setPromoFilter] = useState("all");
  const [promoSel, setPromoSel] = useState(new Set());

  const visiblePromos = promos.filter((p) =>
    promoFilter === "all" || (promoFilter === "live" ? p.active : !p.active));

  const togglePromo = (id) =>
    setPromoSel((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });

  const bulkDelete = async () => {
    const ids = [...promoSel];
    if (!window.confirm(`Permanently delete ${ids.length} selected promo(s)? This cannot be undone.`)) return;
    try {
      const { data } = await api.post("/admin/promos/bulk", { ids, action: "delete" });
      toast.success(`${data.done} promo(s) deleted`);
      setPromoSel(new Set());
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const load = () => api.get("/admin/promos").then((r) => setPromos(r.data)).catch(() => {});
  const loadDrawing = () => api.get("/admin/referrals").then((r) => setDrawing(r.data)).catch(() => {});
  useEffect(() => { load(); loadDrawing(); }, []);

  const draw = async () => {
    setDrawBusy(true);
    setWinner(null);
    try {
      const { data } = await api.post("/admin/referrals/draw");
      setWinner(data);
      toast.success(`${data.winner} wins the Pastinni dinner!`);
      loadDrawing();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setDrawBusy(false);
    }
  };

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/promos", { ...form, active: false });
      toast.success("Promo saved — flip it live when you're ready");
      setForm(empty);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (p) => {
    try {
      await api.patch(`/admin/promos/${p.id}`, { title: p.title, body: p.body, cta_text: p.cta_text, cta_url: p.cta_url, active: !p.active });
      toast.success(p.active ? "Promo hidden from the site" : "Promo is live on the landing page");
      load();
    } catch {
      toast.error("Couldn't update that promo");
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete the promo "${p.title}"?`)) return;
    await api.delete(`/admin/promos/${p.id}`);
    load();
  };

  return (
    <div data-testid="promos-tab" className="mt-8 grid gap-8 lg:grid-cols-2">
      <div>
        <h3 className="font-display text-lg font-bold text-ink">Promos</h3>
        <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.15em] text-slate-400">
          live ones show on the landing page
        </p>
        <div className="mt-4 space-y-3" data-testid="promos-list">
          <div className="flex flex-wrap items-center gap-2" data-testid="promo-filters">
            {[["all", "All"], ["live", "Live"], ["draft", "Drafts"]].map(([key, label]) => (
              <button
                key={key}
                onClick={() => setPromoFilter(key)}
                data-testid={`promo-filter-${key}`}
                className={`rounded-full px-4 py-2 text-xs font-semibold transition-all duration-200 ${
                  promoFilter === key ? "bg-ink text-white shadow-md" : "border border-slate-200 bg-white text-slate-500 hover:border-royal hover:text-royal"
                }`}
              >
                {label}
              </button>
            ))}
            {visiblePromos.length > 0 && (
              <label className="ml-auto flex cursor-pointer items-center gap-2 text-xs font-semibold text-slate-500">
                <input
                  type="checkbox"
                  checked={visiblePromos.length > 0 && visiblePromos.every((p) => promoSel.has(p.id))}
                  onChange={() =>
                    setPromoSel((s) => {
                      const ids = visiblePromos.map((p) => p.id);
                      return ids.every((id) => s.has(id)) ? new Set() : new Set(ids);
                    })
                  }
                  data-testid="promos-select-all"
                  className="h-4 w-4 accent-[#1D4ED8]"
                />
                Select all
              </label>
            )}
          </div>
          <BulkBar count={promoSel.size} onClear={() => setPromoSel(new Set())} testId="bulk-promos-bar">
            <button onClick={bulkDelete} data-testid="bulk-promos-delete" className={`${bulkBtnCls} hover:border-red-300 hover:text-red-600`}>
              Delete selected
            </button>
          </BulkBar>
          {visiblePromos.length === 0 && (
            <p className="rounded-2xl border border-dashed border-slate-200 bg-white/60 px-5 py-8 text-center text-sm text-slate-400">
              No promos in this view.
            </p>
          )}
          {visiblePromos.map((p) => (
            <div key={p.id} className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`promo-row-${p.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex min-w-0 items-start gap-3">
                  <input
                    type="checkbox"
                    checked={promoSel.has(p.id)}
                    onChange={() => togglePromo(p.id)}
                    data-testid={`promo-select-${p.id}`}
                    className="mt-1 h-4 w-4 shrink-0 accent-[#1D4ED8]"
                  />
                  <p className="font-semibold text-ink">{p.title}</p>
                </div>
                <span className={`rounded-full px-2.5 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.12em] ${p.active ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-500"}`} data-testid={`promo-status-${p.id}`}>
                  {p.active ? "Live on site" : "Draft"}
                </span>
              </div>
              {p.body && <p className="mt-1 text-xs text-slate-500">{p.body}</p>}
              {p.cta_text && <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.12em] text-royal">{p.cta_text} → {p.cta_url}</p>}
              <div className="mt-3 flex gap-2">
                <button onClick={() => toggle(p)} data-testid={`promo-toggle-${p.id}`}
                        className={`flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-semibold transition-colors duration-200 ${p.active ? "border border-slate-200 text-slate-600 hover:border-amber-400 hover:text-amber-700" : "bg-royal text-white hover:bg-royal-dark"}`}>
                  <Power className="h-3.5 w-3.5" /> {p.active ? "Take down" : "Go live"}
                </button>
                <button onClick={() => remove(p)} data-testid={`promo-delete-${p.id}`}
                        className="grid h-9 w-9 place-items-center rounded-full border border-slate-200 text-slate-400 transition-colors duration-200 hover:border-red-300 hover:text-red-600">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div>
        <h3 className="font-display text-lg font-bold text-ink">Create a quick promo</h3>
        <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.15em] text-slate-400">
          e.g. "Refer a friend — get entered to win…"
        </p>
        <form onSubmit={create} className="mt-4 space-y-3 rounded-2xl border border-slate-200 bg-white p-6" data-testid="promo-form">
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                 placeholder="Headline *" data-testid="promo-title-input" className={inputCls} required />
          <textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })}
                    placeholder="A line or two of detail (optional)" rows={2} data-testid="promo-body-input"
                    className={`${inputCls} resize-none py-3`} />
          <div className="grid grid-cols-2 gap-3">
            <input value={form.cta_text} onChange={(e) => setForm({ ...form, cta_text: e.target.value })}
                   placeholder="Button text (optional)" data-testid="promo-cta-text-input" className={inputCls} />
            <input value={form.cta_url} onChange={(e) => setForm({ ...form, cta_url: e.target.value })}
                   placeholder="Button link, e.g. /catalog" data-testid="promo-cta-url-input" className={inputCls} />
          </div>
          <button type="submit" disabled={busy} data-testid="promo-create-button"
                  className="flex w-full items-center justify-center gap-2 rounded-xl bg-royal py-3.5 text-sm font-semibold text-white transition-all duration-200 hover:bg-royal-dark disabled:opacity-50">
            <Megaphone className="h-4 w-4" /> {busy ? "Saving…" : "Save promo"}
          </button>
        </form>
      </div>

      <div className="rounded-3xl border border-gold/40 bg-white p-6 lg:col-span-2" data-testid="referral-drawing-card">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h3 className="flex items-center gap-2 font-display text-lg font-bold text-ink">
              <Trophy className="h-5 w-5 text-gold" /> Referral drawing — Pastinni dinner ($100)
            </h3>
            <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.15em] text-slate-400">
              every confirmed booking that names a referrer = one entry in the hat
            </p>
          </div>
          <button onClick={draw} disabled={drawBusy || drawing.total_entries === 0}
                  data-testid="draw-winner-button"
                  className="flex items-center gap-2 rounded-full bg-ink px-6 py-3 text-sm font-semibold text-white transition-all duration-200 hover:bg-royal disabled:opacity-40">
            <PartyPopper className="h-4 w-4" /> {drawBusy ? "Drawing…" : "Draw Pastinni winner"}
          </button>
        </div>

        {winner && (
          <div className="mt-5 rounded-2xl bg-royal px-6 py-6 text-center text-white" data-testid="draw-result">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/70">And the winner is</p>
            <p className="mt-1 font-display text-3xl font-extrabold" data-testid="draw-winner-name">{winner.winner}</p>
            <p className="mt-1 text-xs text-white/80">
              {winner.winner_email ? `Reach them at ${winner.winner_email} · ` : ""}
              drawn from {winner.entries} {winner.entries === 1 ? "entry" : "entries"} across {winner.pool_size} {winner.pool_size === 1 ? "referrer" : "referrers"}
            </p>
          </div>
        )}

        <div className="mt-5" data-testid="referral-pool">
          {drawing.pool.length === 0 ? (
            <p className="rounded-2xl border border-dashed border-slate-200 bg-white/60 px-5 py-8 text-center text-sm text-slate-400" data-testid="referral-pool-empty">
              No entries yet — the hat fills up as referred friends complete bookings.
            </p>
          ) : (
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {drawing.pool.map((e, i) => (
                <div key={e.referrer} className="flex items-center justify-between rounded-xl border border-slate-200 bg-paper px-4 py-3" data-testid={`referral-entry-${i}`}>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-ink">{e.referrer}</p>
                    {e.email && <p className="truncate text-[10px] text-slate-400">{e.email}</p>}
                  </div>
                  <span className="ml-3 shrink-0 rounded-full bg-royal-light px-2.5 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-royal">
                    {e.entries} {e.entries === 1 ? "entry" : "entries"}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {drawing.last_draw && !winner && (
          <p className="mt-4 text-xs text-slate-400" data-testid="last-draw-note">
            Last drawing: <span className="font-semibold text-slate-600">{drawing.last_draw.winner}</span> won
            from {drawing.last_draw.entries} entries · drawn by {drawing.last_draw.drawn_by} · {new Date(drawing.last_draw.at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
          </p>
        )}
      </div>
    </div>
  );
};
